/* eslint-disable @typescript-eslint/no-explicit-any -- GrapesJS has no TypeScript types */
import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import grapesjsPresetNewsletter from "grapesjs-preset-newsletter";
import myPlugin from "my-grapesjs-plugin";
import newsletterTemplate from "../emailTemplates/newsletter-template";
import newsletterTemplateNewUser from "../emailTemplates/newsletter-template_newUser";
import newsletterStyles from "../emailTemplates/newsletter-css";
import { useAppSelector } from "../redux/hooks";
import { selectUserRole } from "../redux/user";
import { isReadOnlyUser } from "./utils/roleHelpers";
import { normalizeHtmlPlaceholders } from "./utils/normalizeHtmlPlaceholders";

export interface TemplateMetadata {
  templateId: string;
  templateName: string;
  folderName?: string;
  accountId?: string;
  clientId?: string;
  templateCategory?: string;
  userUuid?: string;
}

export interface EditorRef {
  loadHtml: (html: string) => void;
  resetToDefault: () => void;
  getHtml: () => string;
  setMetadata: (metadata: TemplateMetadata) => void;
}

export interface ImportedTemplateMetadata {
  templateId?: string;
  templateName?: string;
  templateFolder?: string;
  templateCategory?: string;
  account?: string;
  client?: string;
}

export interface EditorImportPayload {
  cleanHtml: string;
  hasTemplateTags: boolean;
  metadata: ImportedTemplateMetadata;
}

interface EditorProps {
  readOnly?: boolean;
  onOpenLibrary?: () => void;
  onTemplateImported?: (payload: EditorImportPayload) => void;
}

const RESTRICTED_PANEL_VISIBILITY = {
  panels: {
    options: false,
    views: false,
    "devices-c": false,
    commands: false,
  },
  buttons: {
    commands: {
      "import-template-btn": true,
    },
  },
} as const;

const FULL_PANEL_VISIBILITY = {
  panels: {
    options: true,
    views: true,
    "devices-c": true,
    commands: true,
  },
  buttons: {
    commands: {
      "import-template-btn": true,
    },
  },
} as const;

const RESTRICTED_BLOCK_VISIBILITY = false;
const FULL_BLOCK_VISIBILITY = true;

const Editor = forwardRef<EditorRef, EditorProps>(
  ({ readOnly = false, onOpenLibrary, onTemplateImported }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const editorInstanceRef = useRef<any>(null);
    const readOnlyRef = useRef(readOnly);
    const location = useLocation();
    const userRole = useAppSelector(selectUserRole);
    const onOpenLibraryRef = useRef(onOpenLibrary);
    const onTemplateImportedRef = useRef(onTemplateImported);
    const canvasClickHandlerRef = useRef<((event: Event) => void) | null>(null);
    const componentSelectBlockerRef = useRef<
      ((model: any, opts: any) => void) | null
    >(null);
    const componentMountHandlerRef = useRef<((component: any) => void) | null>(
      null,
    );
    const openLibraryBoundElementsRef = useRef(new WeakSet<HTMLElement>());

    useEffect(() => {
      onOpenLibraryRef.current = onOpenLibrary;
    }, [onOpenLibrary]);

    useEffect(() => {
      onTemplateImportedRef.current = onTemplateImported;
    }, [onTemplateImported]);

    const syncTableCellReadOnly = (editor: any, isReadOnly: boolean) => {
      const root = editor.getWrapper?.();
      if (!root) {
        return;
      }

      const lockComponent = (component: any) => {
        const tagName = String(component.get?.("tagName") || "").toLowerCase();
        if (tagName === "td" || tagName === "th") {
          component.set({ locked: isReadOnly });
        }
        component.components?.().forEach((child: any) => lockComponent(child));
      };
      lockComponent(root);
    };

    const wrapLooseTableCellTextNodes = (html: string): string => {
      if (typeof DOMParser === "undefined") {
        return html;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const cellNodes = doc.querySelectorAll("td, th");

      cellNodes.forEach((cell) => {
        const children = Array.from(cell.childNodes);
        children.forEach((node) => {
          if (node.nodeType !== Node.TEXT_NODE) {
            return;
          }

          const text = node.textContent || "";
          if (!text.trim()) {
            return;
          }

          const span = doc.createElement("span");
          span.textContent = text;
          cell.insertBefore(span, node);
          cell.removeChild(node);
        });
      });

      return doc.body.innerHTML;
    };

    const unlockTableCellContent = (editor: any) => {
      const root = editor.getWrapper?.();
      if (!root) {
        return;
      }

      const shouldUnlock = (component: any) => {
        const componentType = String(component.get?.("type") || "").toLowerCase();
        if (componentType === "text" || componentType === "textnode") {
          return true;
        }

        const tagName = String(component.get?.("tagName") || "").toLowerCase();
        return ["td", "th", "p", "span", "strong", "em", "b", "i", "u", "a", "li", "h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName);
      };

      const walk = (component: any) => {
        if (shouldUnlock(component)) {
          component.set({
            editable: true,
            selectable: true,
          });
        }
        component.components?.().forEach((child: any) => walk(child));
      };

      walk(root);
    };

    const syncReadOnlyMode = (editor: any, isReadOnly: boolean) => {
      readOnlyRef.current = isReadOnly;
      editor.setDragMode(isReadOnly ? "absolute" : "translate");
      syncTableCellReadOnly(editor, isReadOnly);

      const canvasBody = editor.Canvas.getBody();
      if (canvasBody) {
        canvasBody.style.pointerEvents = "auto";
      }

      const canvasWindow = editor.Canvas.getWindow?.();
      const canvasDocument = canvasWindow?.document;
      if (canvasDocument && canvasClickHandlerRef.current) {
        canvasDocument.removeEventListener(
          "click",
          canvasClickHandlerRef.current,
          false,
        );
        canvasClickHandlerRef.current = null;
      }

      if (isReadOnly && onOpenLibraryRef.current && canvasDocument) {
        canvasClickHandlerRef.current = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof (event as any).stopImmediatePropagation === "function") {
            (event as any).stopImmediatePropagation();
          }
          onOpenLibraryRef.current?.();
        };
        canvasDocument.addEventListener(
          "click",
          canvasClickHandlerRef.current,
          false,
        );
      }

      if (componentSelectBlockerRef.current) {
        editor.off(
          "component:select:before",
          componentSelectBlockerRef.current,
        );
        componentSelectBlockerRef.current = null;
      }

      if (isReadOnly) {
        componentSelectBlockerRef.current = (_model: any, opts: any) => {
          if (opts) {
            opts.abort = true;
          }
        };
        editor.on("component:select:before", componentSelectBlockerRef.current);
      }

      const stylesPanel = document.querySelector(".gjs-sm-sector");
      if (stylesPanel?.parentElement) {
        stylesPanel.parentElement.style.display = isReadOnly ? "none" : "";
      }
    };

    const restrictedPanelVisibility = useMemo(() => {
      const isGeneralReadOnly = isReadOnlyUser(userRole);
      const canUseCommandsPanel = !isGeneralReadOnly;

      return {
        ...RESTRICTED_PANEL_VISIBILITY,
        panels: {
          ...RESTRICTED_PANEL_VISIBILITY.panels,
          commands: canUseCommandsPanel,
        },
        buttons: {
          ...RESTRICTED_PANEL_VISIBILITY.buttons,
          commands: {
            ...RESTRICTED_PANEL_VISIBILITY.buttons.commands,
            "import-template-btn": canUseCommandsPanel,
          },
        },
      };
    }, [userRole]);

    const applyTemplateSelectionVisibility = (
      editor: any,
      hasTemplateName: boolean,
    ) => {
      const panelVisibility = hasTemplateName
        ? FULL_PANEL_VISIBILITY
        : restrictedPanelVisibility;
      const blockVisibility = hasTemplateName
        ? FULL_BLOCK_VISIBILITY
        : RESTRICTED_BLOCK_VISIBILITY;

      editor.__applyPanelVisibility?.(panelVisibility);
      editor.__applyBlockVisibility?.(blockVisibility);

      // GrapesJS can re-render the block manager after this call, so apply it again
      // on the next frame to keep FULL_BLOCK_VISIBILITY from being overwritten.
      requestAnimationFrame(() => {
        editor.__applyBlockVisibility?.(blockVisibility);
      });
    };

    // Select template based on user role
    const defaultTemplate = useMemo(
      () =>
        isReadOnlyUser(userRole)
          ? newsletterTemplateNewUser
          : newsletterTemplate,
      [userRole],
    );

    // Expose loadHtml and resetToDefault methods to parent
    useImperativeHandle(ref, () => ({
      loadHtml: (html: string) => {
        if (editorInstanceRef.current) {
          const editor = editorInstanceRef.current;

          // Parse the HTML document
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          // Extract CSS from <style> tags
          const styleTags = doc.querySelectorAll("style");
          let cssContent = "";
          styleTags.forEach((tag) => {
            cssContent += tag.textContent || "";
          });

          // Extract body HTML (or use original HTML if no body tag)
          const rawBodyHtml = doc.body ? doc.body.innerHTML : html;

          // Sanitize body HTML before loading into the editor to strip
          // any XSS vectors while preserving all email-safe markup
          const bodyHtml = DOMPurify.sanitize(rawBodyHtml, {
            ADD_TAGS: ["style"],
            ADD_ATTR: [
              "target",
              "bgcolor",
              "align",
              "valign",
              "cellpadding",
              "cellspacing",
              "border",
            ],
            FORCE_BODY: true,
          });

         const normalizedBodyHtml = normalizeHtmlPlaceholders(bodyHtml, { mode: "toComputed" })

          // Check if HTML has inline styles (style attributes)
          const hasInlineStyles = normalizedBodyHtml.includes("style=");

          // Clear existing components and styles first
          editor.setComponents("");
          editor.setStyle("");

          // Decide which CSS to use:
          // 1. If template has CSS in style tags, use it
          // 2. If template has inline styles, don't add any CSS (inline styles will handle it)
          // 3. Otherwise, use base newsletter styles
          if (cssContent && cssContent.trim()) {
            editor.setStyle(cssContent);
          } else if (!hasInlineStyles) {
            editor.setStyle(newsletterStyles);
          }

          // Wrap loose text nodes in table cells so text after inline tags
          // (eg. </strong> plain text) is editable in GrapesJS.
          const normalizedEditableBodyHtml =
            wrapLooseTableCellTextNodes(normalizedBodyHtml);

          // Set the sanitized body HTML as components
          editor.setComponents(normalizedEditableBodyHtml);
          unlockTableCellContent(editor);
        }
      },
      resetToDefault: () => {
        if (editorInstanceRef.current) {
          const editor = editorInstanceRef.current;
          editor.setComponents(wrapLooseTableCellTextNodes(defaultTemplate));
          editor.setStyle(newsletterStyles);
          unlockTableCellContent(editor);
          applyTemplateSelectionVisibility(editor, false);
        }
      },
      setMetadata: (metadata: TemplateMetadata) => {
        if (editorInstanceRef.current) {
          const editor = editorInstanceRef.current as any;
          editor.__templateMetadata = metadata;
          applyTemplateSelectionVisibility(
            editor,
            Boolean(metadata.templateName?.trim()),
          );
        }
      },
      getHtml: () => {
        if (editorInstanceRef.current) {
          // Get both HTML and CSS from the editor
          const html = editorInstanceRef.current.getHtml();
          const css = editorInstanceRef.current.getCss();

          // Return complete HTML document with styles
          return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Template</title>
  <style>
${css}
  </style>
</head>
<body>
${html}
</body>
</html>`;
        }
        return "";
      },
    }));

    useEffect(() => {
      if (!editorRef.current || editorInstanceRef.current) return;

      // Get HTML from location state if available
      const templateHtml =
        (location.state as any)?.templateHtml || defaultTemplate;

      // Register plugins (handle ESM/CJS interop for default exports)
      const resolvedNewsletter =
        (grapesjsPresetNewsletter as any).default ?? grapesjsPresetNewsletter;
      const resolvedMyPlugin = (myPlugin as any).default ?? myPlugin;
      grapesjs.plugins.add("gjs-newsletter", resolvedNewsletter);
      grapesjs.plugins.add("gjs-myPlugin", resolvedMyPlugin);

      const editor = grapesjs.init({
        container: editorRef.current,
        height: "100%",
        fromElement: false,
        storageManager: false,
        plugins: ["gjs-newsletter", "gjs-myPlugin"],
        pluginsOpts: {
          "gjs-newsletter": {
            modalLabelImport: "Paste all your code here below and click import",
            modalLabelExport: "Copy the code and use it wherever you want",
            cellStyle: {
              "font-size": "12px",
              "font-weight": 300,
              "vertical-align": "top",
              color: "rgb(111, 119, 125)",
              margin: 0,
              padding: 0,
            },
          },
          "gjs-myPlugin": {
            showBlocksOnLoad: true,
            showStylesOnChange: true,
            onImport: (payload: EditorImportPayload) => {
              onTemplateImportedRef.current?.(payload);
            },
            cmdImportButtonId: "import-template-btn",
            // Initial page load is restricted (before template name selection).
            panelVisibility: restrictedPanelVisibility,
            blockVisibility: RESTRICTED_BLOCK_VISIBILITY,
          },
        },
        assetManager: {
          upload: false,
          autoAdd: true, // Automatically add assets when components are added
        },
        canvas: {
          styles: [],
          scripts: [],
        },
      });

      const applyCanvasBorder = () => {
        const container = editor.getContainer();
        const canvasEl = container?.querySelector(".gjs-cv-canvas");
        if (canvasEl instanceof HTMLElement) {
          canvasEl.style.boxSizing = "border-box";
          canvasEl.style.overflowY = "auto";
          canvasEl.style.overflowX = "hidden";
          canvasEl.style.paddingBottom = "80px";
        }

        // Defensive: Only call getDocument if it exists
        if (editor.Canvas && typeof editor.Canvas.getDocument === "function") {
          const canvasDoc = editor.Canvas.getDocument();
          if (canvasDoc?.body) {
            canvasDoc.body.style.margin = "0";
            canvasDoc.body.style.paddingBottom = "80px";
            canvasDoc.body.style.overflowY = "auto";
            canvasDoc.body.style.overflowX = "hidden";
          }
        }
      };

      editor.on("load", applyCanvasBorder);
      requestAnimationFrame(applyCanvasBorder);

      editor.on('load', () => {
        const canvas = editor.Canvas;
        const iframe = canvas.getFrameEl();
        if (iframe && iframe.contentDocument) {
          const style = iframe.contentDocument.createElement('style');
          style.innerHTML = `
      /* Webkit browsers (Chrome, Edge, Safari) */
      ::-webkit-scrollbar {
        width: 15px;
        background: white;
      }
      ::-webkit-scrollbar-thumb {
        background: #fff;
        border: 1px solid black;
      }
      /* Firefox */
      body {
        scrollbar-color: #fff #fff;
        scrollbar-width: thin;
      }
    `;
          iframe.contentDocument.head.appendChild(style);
        }
      });

      editor.setComponents(wrapLooseTableCellTextNodes(templateHtml));
      unlockTableCellContent(editor);
      editor.setStyle(newsletterStyles);
      syncTableCellReadOnly(editor, readOnlyRef.current);
      syncReadOnlyMode(editor, readOnly);

      componentMountHandlerRef.current = (component: any) => {
        const tagName = String(component.get?.("tagName") || "").toLowerCase();
        if (tagName === "td" || tagName === "th") {
          component.set({ locked: readOnlyRef.current });
        }

        const el = component.getEl();
        const action = component.getAttributes?.()["data-action"];
        if (
          action === "open-library" &&
          el instanceof HTMLElement &&
          !openLibraryBoundElementsRef.current.has(el)
        ) {
          el.addEventListener("click", (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenLibraryRef.current?.();
          });
          openLibraryBoundElementsRef.current.add(el);
        }
      };
      editor.on("component:mount", componentMountHandlerRef.current);

      editorInstanceRef.current = editor;

      return () => {
        if (componentMountHandlerRef.current) {
          editor.off("component:mount", componentMountHandlerRef.current);
          componentMountHandlerRef.current = null;
        }

        if (componentSelectBlockerRef.current) {
          editor.off(
            "component:select:before",
            componentSelectBlockerRef.current,
          );
          componentSelectBlockerRef.current = null;
        }

        const canvasDocument = editor.Canvas.getWindow?.()?.document;
        if (canvasDocument && canvasClickHandlerRef.current) {
          canvasDocument.removeEventListener(
            "click",
            canvasClickHandlerRef.current,
            false,
          );
          canvasClickHandlerRef.current = null;
        }

        editor.destroy();
        editorInstanceRef.current = null;
      };
    }, []);

    // Update template when user role changes
    useEffect(() => {
      if (!editorInstanceRef.current) return;

      // Only update if there's no location state (i.e., not loading a saved template)
      if ((location.state as any)?.templateHtml) return;

      editorInstanceRef.current.setComponents(
        wrapLooseTableCellTextNodes(defaultTemplate),
      );
      unlockTableCellContent(editorInstanceRef.current);
      editorInstanceRef.current.setStyle(newsletterStyles);
      applyTemplateSelectionVisibility(editorInstanceRef.current, false);
    }, [userRole, defaultTemplate, location.state]);

    // Handle readOnly mode changes
    useEffect(() => {
      if (!editorInstanceRef.current) return;

      const editor = editorInstanceRef.current;
      syncReadOnlyMode(editor, readOnly);
    }, [readOnly, onOpenLibrary]);

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          id="gjs"
          ref={editorRef}
          style={{ flex: "1 1 auto", minHeight: 0 }}
        />
      </div>
    );
  },
);

Editor.displayName = "Editor";

export default Editor;
